import { useState, useEffect, useContext } from 'react';
import { Chains } from 'api/types/httpsTypes/d-wallets';
import { useQuery } from '@tanstack/react-query';
import {
  blockchainInfoKeys,
  BlockchainInfoService,
} from 'api/services/httpServices/BlockchainInfoService';
import { WebSocketContext } from 'api/services/websocket/websocketProvider';

export interface ChainInfoMessage {
  p: string; // Chain Asset Price
  c: Chains; // Chain
  b: number; // Block
  g: number; // Gas
}

export interface ChainInfoProps {
  price: string | null | undefined;
  blockNumber: number | null | undefined;
  gasPrice: number | null | undefined;
}

export const useChainInfo = (chain: Chains): ChainInfoProps | null => {
  const { data: gasInfo } = useQuery({
    queryKey: blockchainInfoKeys.getGasInfoWithChain(chain),
    queryFn: () => BlockchainInfoService.getGasInfoWithChain(chain),
  });

  const chainInfo = useChainInfoWebsocket(chain);

  return {
    price: chainInfo ? chainInfo.p : gasInfo?.price ?? null,
    gasPrice: chainInfo ? chainInfo.g : gasInfo ? gasInfo.gasPrice : null,
    blockNumber: chainInfo ? chainInfo.b : gasInfo?.blockNumber ?? null,
  };
};

export const useChainInfoWebsocket = (
  chain: Chains
): ChainInfoMessage | null => {
  const wsContext = useContext(WebSocketContext);
  const [chainInfo, setChainInfo] = useState<ChainInfoMessage | null>(null);

  useEffect(() => {
    if (!wsContext) return;

    const dataKey = { chain };

    // ✅ Get latest cached message
    const cached = wsContext.getLatestMessage('chain_info', dataKey);
    if (cached?.data) {
      setChainInfo(cached.data as ChainInfoMessage);
    }

    // ✅ Listen to new messages
    const handleUpdate = (msg: any) => {
      const parsed = msg.data as ChainInfoMessage;

      if (parsed.c === chain) {
        setChainInfo(parsed);
      }
    };

    wsContext.addMessageListener('chain_info', dataKey, handleUpdate);

    return () => {
      wsContext.removeMessageListener('chain_info', dataKey, handleUpdate);
    };
  }, [wsContext, chain]);

  return chainInfo;
};
